"use client";

import { useMemo, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  getListedAfterTimestamp,
  type UseListingBrowseProductsArgs,
} from "./listing-browse-query";

export function useListingBrowseQueryArgs(
  args: UseListingBrowseProductsArgs,
  page: number,
) {
  const values = useListingBrowseQueryValues(args);
  const searchArgs = useSearchArgs(args, values);
  const nearbyArgs = useNearbyArgs(args, values, page);
  const fallbackArgs = useFallbackArgs(args, values, page);

  return { ...values, searchArgs, nearbyArgs, fallbackArgs };
}

function intentArgFor(args: UseListingBrowseProductsArgs) {
  if (args.saleOnly) return "sell" as const;
  return args.intentFilter === "all" ? undefined : args.intentFilter;
}

function useListingBrowseQueryValues(args: UseListingBrowseProductsArgs) {
  const [browseStartedAt] = useState(Date.now);
  const stateDoc = useQuery(
    api.states.queries.getByCode,
    args.stateCode ? { code: args.stateCode } : "skip",
  );
  const listedAfterDaysArg =
    args.listedAfterDays !== null && args.listedAfterDays > 0
      ? args.listedAfterDays
      : undefined;
  const listedAfter = useMemo(
    () => getListedAfterTimestamp(args.listedAfterDays, browseStartedAt),
    [args.listedAfterDays, browseStartedAt],
  );

  const isFreePickupFilter = args.intentFilter === "recycle";

  return {
    hasLocation:
      args.location?.lat !== undefined && args.location?.lng !== undefined,
    hasSearch: args.searchQuery.trim().length > 0,
    intentArg: intentArgFor(args),
    stateId: stateDoc?._id,
    stateLookupPending: args.stateCode !== null && stateDoc === undefined,
    apiSpecFilters:
      args.specFilters.length === 0
        ? undefined
        : args.specFilters.map((filter) => ({
            field: filter.field,
            operator: "eq" as const,
            value: filter.value,
          })),
    priceMinCents: isFreePickupFilter
      ? undefined
      : args.priceRange.min === null
        ? undefined
        : Math.round(args.priceRange.min * 100),
    priceMaxCents: isFreePickupFilter
      ? 0
      : args.priceRange.max === null
        ? undefined
        : Math.round(args.priceRange.max * 100),
    listedAfterDaysArg,
    listedAfter,
  };
}

type QueryValues = ReturnType<typeof useListingBrowseQueryValues>;

function useSearchArgs(
  args: UseListingBrowseProductsArgs,
  values: QueryValues,
) {
  return useMemo(() => {
    if (!values.hasSearch || (args.stateCode && !values.stateId)) {
      return "skip" as const;
    }
    return {
      searchText: args.searchQuery.trim(),
      latitude: values.hasLocation ? args.location!.lat : undefined,
      longitude: values.hasLocation ? args.location!.lng : undefined,
      radiusMiles: values.hasLocation ? args.radiusMiles : undefined,
      stateId: values.stateId,
      productTypeSlug: args.productType ?? undefined,
      conditions: args.conditions.length > 0 ? args.conditions : undefined,
      priceMinCents: values.priceMinCents,
      priceMaxCents: values.priceMaxCents,
      specFilters: values.apiSpecFilters,
      listedAfter: values.listedAfter,
      intent: values.intentArg,
      palletCount: args.quantity || 1,
    };
  }, [args, values]);
}

function useNearbyArgs(
  args: UseListingBrowseProductsArgs,
  values: QueryValues,
  page: number,
) {
  return useMemo(() => {
    if (!values.hasLocation || values.hasSearch) return "skip" as const;
    return {
      buyerLat: args.location!.lat,
      buyerLon: args.location!.lng,
      buyerZip: args.location!.postalCode || undefined,
      buyerState: args.location!.state,
      radiusMiles: args.radiusMiles ?? 200,
      productTypeSlug: args.productType ?? undefined,
      palletCount: args.quantity || 1,
      page,
      perPage: 60,
      includeTotal: true,
      conditions: args.conditions.length > 0 ? args.conditions : undefined,
      priceMinCents: values.priceMinCents,
      priceMaxCents: values.priceMaxCents,
      specFilters: values.apiSpecFilters,
      listedAfterDays: values.listedAfterDaysArg,
      intent: values.intentArg,
    };
  }, [args, page, values]);
}

function useFallbackArgs(
  args: UseListingBrowseProductsArgs,
  values: QueryValues,
  page: number,
) {
  return useMemo(() => {
    if (values.hasLocation || values.hasSearch) return "skip" as const;
    if (args.stateCode && !values.stateId) return "skip" as const;
    return {
      productTypeSlug: args.productType ?? undefined,
      stateId: values.stateId,
      page,
      perPage: 60,
      includeTotal: true,
      conditions: args.conditions.length > 0 ? args.conditions : undefined,
      priceMinCents: values.priceMinCents,
      priceMaxCents: values.priceMaxCents,
      specFilters: values.apiSpecFilters,
      listedAfterDays: values.listedAfterDaysArg,
      intent: values.intentArg,
      searchQuery: args.searchQuery || undefined,
      palletCount: args.quantity || 1,
    };
  }, [args, page, values]);
}

export function useSmartFallbackArgs(
  args: UseListingBrowseProductsArgs,
  values: QueryValues,
  shouldUseSmartFallback: boolean,
) {
  return useMemo(() => {
    if (!shouldUseSmartFallback) return "skip" as const;
    return {
      buyerLat: args.location!.lat,
      buyerLon: args.location!.lng,
      buyerZip: args.location!.postalCode || undefined,
      buyerState: args.location!.state,
      initialRadiusMiles: args.radiusMiles ?? 200,
      productTypeSlug: args.productType ?? undefined,
      palletCount: args.quantity || 1,
      limit: 60,
      conditions: args.conditions.length > 0 ? args.conditions : undefined,
      priceMinCents: values.priceMinCents,
      priceMaxCents: values.priceMaxCents,
      specFilters: values.apiSpecFilters,
      listedAfterDays: values.listedAfterDaysArg,
      intent: values.intentArg,
    };
  }, [args, shouldUseSmartFallback, values]);
}
